import Grid from '@mui/material/Grid';
import { Dispatch, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { AnyAction } from 'redux';
import {
  findEnvelopeDetailsReportRequested,
  masterDataSelector,
  reportEnvelopeDetailsReportLoadingSelector,
  reportCollectionsFilterStartBusinessDateSelector,
  reportCollectionsFilterEndBusinessDateSelector,
  reportFilterCountryCodeSelector,
  changeFilterCountry,
  changeFilterStore,
  changeCollectionsFilterEndBusinessDate,
  changeCollectionsFilterStartBusinessDate,
} from '../state';

import {
  CountryFilter,
  ReportActionsGridItem,
  ReportFilterActions,
  ReportFilters,
  StoreFilter,
} from './ReportFilters';
import { AppPermissions, MasterData, RemoteResource } from '../types';
import { BusinessDateRangeFilter } from './CollectionsFilters';
import { PermissionsProvider } from './Permissions';
import { dateResponseMap } from '../helpers';
import { FilterPanel, StatusChip } from './Visual';

const handleSearchParams = (
  dispatch: Dispatch<AnyAction>,
  searchParams: URLSearchParams,
  masterData: RemoteResource<MasterData | undefined>
) => {
  const countryCode = searchParams.get('country');
  const storeAcronym = searchParams.get('store');
  const businessDate = searchParams.get('businessDate');
  const movementCode = searchParams.get('movementCode');

  if (countryCode) {
    const country = masterData.data?.countries?.find(c => c.countryCode === countryCode);
    if (country) {
      dispatch(changeFilterCountry(country.countryCode));
    }
  }

  if (storeAcronym) {
    const store = masterData.data?.stores?.find(s => s.storeAcronym === storeAcronym);
    if (store) {
      dispatch(changeFilterStore([store]));
    }
  }

  if (businessDate) {
    const parsedBusinessDate = dateResponseMap(businessDate) as Date;
    dispatch(changeCollectionsFilterStartBusinessDate(parsedBusinessDate));
    dispatch(changeCollectionsFilterEndBusinessDate(parsedBusinessDate));
  }

  if (movementCode) {
    dispatch(findEnvelopeDetailsReportRequested(movementCode ?? undefined));
  }
};

const EnvelopeDetailsReportSearch = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const masterData = useSelector(masterDataSelector);
  const [searchParams] = useSearchParams();
  const startDate = useSelector(reportCollectionsFilterStartBusinessDateSelector);
  const endDate = useSelector(reportCollectionsFilterEndBusinessDateSelector);
  const hasCountry = Boolean(useSelector(reportFilterCountryCodeSelector));
  const applyDisabled = !(startDate.accept && endDate.accept && hasCountry);
  const countryRequiredIncomplete = applyDisabled && !hasCountry;
  const businessDateRequiredIncomplete = applyDisabled && !(startDate.accept && endDate.accept);

  useEffect(() => {
    handleSearchParams(dispatch, searchParams, masterData);
  }, [dispatch, searchParams, masterData]);

  return (
    <PermissionsProvider resourcePermissions={[AppPermissions.SALES_TOTALS]}>
      <ReportFilters
        loadingSelector={reportEnvelopeDetailsReportLoadingSelector}
        applyAction={findEnvelopeDetailsReportRequested}
        onedriveAction={() => findEnvelopeDetailsReportRequested(undefined, true)}
      >
        <FilterPanel
          subtitleTranslationKey="envelope-details-report.filters.subtitle"
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <CountryFilter fullWidth requiredIncomplete={countryRequiredIncomplete} />
            </Grid>
            <Grid item xs={12} md={3} lg={3} xl={2}>
              <StoreFilter />
            </Grid>
            <Grid item xs={12} md={6} lg={5} xl={4}>
              <BusinessDateRangeFilter
                spacing={1}
                requiredIncomplete={businessDateRequiredIncomplete}
              />
            </Grid>
            <ReportActionsGridItem>
              <ReportFilterActions applyDisabled={applyDisabled} />
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </ReportFilters>
    </PermissionsProvider>
  );
};

export default EnvelopeDetailsReportSearch;
